import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TechnicalSupportService, TechnicalSupportTicket, TechnicalSupportFilters, TechnicalSupportStats } from '../../../shared/services/technical-support.service';

@Component({
  selector: 'app-support-technical',
  templateUrl: './support-technical.component.html',
  styleUrls: ['./support-technical.component.css']
})
export class SupportTechnicalComponent implements OnInit {
  // Form related
  ticketForm: FormGroup;
  browsers = ['Chrome', 'Firefox', 'Edge', 'Safari', 'Opera', 'Internet Explorer'];
  osList = ['Windows', 'macOS', 'Linux', 'Android', 'iOS', 'Other'];
  selectedFile: File | null = null;
  screenshotFile: File | null = null;
  screenshotPreview: string | ArrayBuffer | null = null;
  loading = false;

  // List related
  tickets: TechnicalSupportTicket[] = [];
  filteredTickets: TechnicalSupportTicket[] = [];
  displayedColumns: string[] = ['ticket_number', 'name', 'email', 'ticket_type', 'priority', 'status', 'created_at', 'actions'];
  
  // Pagination
  currentPage = 1;
  totalPages = 0;
  totalRecords = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Filters
  filters: TechnicalSupportFilters = {
    page: 1,
    limit: 10,
    sort_by: 'created_at',
    sort_order: 'DESC'
  };

  // Filter options
  ticketTypes = ['Feature Request', 'Bug', 'Comment'];
  priorities = ['Urgent', 'Good to Do', 'Low Importance'];
  statuses = ['open', 'in_progress', 'resolved', 'closed'];

  // Stats
  stats: TechnicalSupportStats | null = null;

  // View modes
  viewMode: 'list' | 'create' = 'list';
  editMode = false;
  selectedTicket: TechnicalSupportTicket | null = null;

  constructor(
    private fb: FormBuilder,
    private technicalSupportService: TechnicalSupportService,
    private router: Router,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadTickets();
    this.loadStats();
  }

  initForm(): void {
    this.ticketForm = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      ticket_type: ['Feature Request', Validators.required],
      priority: ['Good to Do', Validators.required],
      browser: ['', Validators.required],
      browser_version: ['', Validators.required],
      os: ['', Validators.required],
      os_version: ['', Validators.required],
      additional_details: ['', [Validators.required, Validators.minLength(10)]],
    });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.screenshotFile = input.files[0];

      // Validate file size (5MB limit)
      if (this.screenshotFile.size > 5 * 1024 * 1024) {
        this.showError('File size must be less than 5MB');
        this.screenshotFile = null;
        input.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(this.screenshotFile.type)) {
        this.showError('Only image, PDF, and document files are allowed');
        this.screenshotFile = null;
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.screenshotPreview = reader.result;
      };
      reader.readAsDataURL(this.screenshotFile);
    }
  }

  removeFile(): void {
    this.screenshotFile = null;
    this.screenshotPreview = null;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit(): void {
    if (this.ticketForm.valid) {
      this.loading = true;
      
      const ticketData: TechnicalSupportTicket = {
        ...this.ticketForm.value
      };

      if (this.editMode && this.selectedTicket) {
        // Update existing ticket
        this.technicalSupportService.updateTicket(this.selectedTicket.id!, ticketData)
          .subscribe({
            next: (response) => {
              this.showSuccess('Technical support ticket updated successfully');
              this.resetForm();
              this.loadTickets();
              this.viewMode = 'list';
            },
            error: (error) => {
              console.error('Error updating ticket:', error);
              this.showError('Failed to update technical support ticket');
              this.loading = false;
            }
          });
      } else {
        // Create new ticket
        this.technicalSupportService.createTicket(ticketData)
          .subscribe({
            next: (response) => {
              const ticketId = response.data.id;
              
              // Upload file if selected
              if (this.screenshotFile && ticketId) {
                this.uploadFile(ticketId);
              } else {
                this.showSuccess('Technical support ticket created successfully');
                this.resetForm();
                this.loadTickets();
                this.viewMode = 'list';
              }
            },
            error: (error) => {
              console.error('Error creating ticket:', error);
              this.showError('Failed to create technical support ticket');
              this.loading = false;
            }
          });
      }
    } else {
      this.ticketForm.markAllAsTouched();
      this.showError('Please fill in all required fields correctly');
    }
  }

  private uploadFile(ticketId: number): void {
    if (!this.screenshotFile) return;

    this.technicalSupportService.uploadFile(ticketId, this.screenshotFile)
      .subscribe({
        next: (response) => {
          this.showSuccess('Technical support ticket created successfully with attachment');
          this.resetForm();
          this.loadTickets();
          this.viewMode = 'list';
        },
        error: (error) => {
          console.error('Error uploading file:', error);
          this.showSuccess('Ticket created but file upload failed');
          this.resetForm();
          this.loadTickets();
          this.viewMode = 'list';
        }
      });
  }

  resetForm(): void {
    this.ticketForm.reset({
      ticket_type: 'Feature Request',
      priority: 'Good to Do'
    });
    this.removeFile();
    this.loading = false;
    this.editMode = false;
    this.selectedTicket = null;
  }

  // ==================== LIST OPERATIONS ====================

  loadTickets(): void {
    this.loading = true;
    this.technicalSupportService.getTickets(this.filters).subscribe({
      next: (response) => {
        this.tickets = response.data.tickets;
        this.filteredTickets = this.tickets;
        this.currentPage = response.data.pagination.current_page;
        this.totalPages = response.data.pagination.total_pages;
        this.totalRecords = response.data.pagination.total_records;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tickets:', error);
        this.showError('Failed to load technical support tickets');
        this.loading = false;
      }
    });
  }

  loadStats(): void {
    this.technicalSupportService.getTicketStats().subscribe({
      next: (response) => {
        this.stats = response.data;
      },
      error: (error) => {
        console.error('Error loading stats:', error);
      }
    });
  }

  onPageChange(event: any): void {
    this.filters.page = event.pageIndex + 1;
    this.filters.limit = event.pageSize;
    this.pageSize = event.pageSize;
    this.loadTickets();
  }

  applyFilters(): void {
    this.filters.page = 1;
    this.loadTickets();
  }

  clearFilters(): void {
    this.filters = {
      page: 1,
      limit: this.pageSize,
      sort_by: 'created_at',
      sort_order: 'DESC'
    };
    this.loadTickets();
  }

  onSearchChange(event: any): void {
    const searchTerm = event.target.value;
    if (searchTerm) {
      this.filters.search = searchTerm;
    } else {
      delete this.filters.search;
    }
    this.applyFilters();
  }

  // ==================== CRUD OPERATIONS ====================

  showCreateForm(): void {
    this.viewMode = 'create';
    this.editMode = false;
    this.resetForm();
  }

  showList(): void {
    this.viewMode = 'list';
    this.editMode = false;
    this.resetForm();
  }

  viewTicket(ticket: TechnicalSupportTicket): void {
    this.selectedTicket = ticket;
    // You can open a dialog here or navigate to a detail page
    this.showSuccess(`Viewing ticket: ${ticket.ticket_number}`);
  }

  editTicket(ticket: TechnicalSupportTicket): void {
    this.selectedTicket = ticket;
    this.editMode = true;
    this.viewMode = 'create';
    
    // Populate form with ticket data
    this.ticketForm.patchValue({
      first_name: ticket.first_name,
      last_name: ticket.last_name,
      email: ticket.email,
      ticket_type: ticket.ticket_type,
      priority: ticket.priority,
      browser: ticket.browser,
      browser_version: ticket.browser_version,
      os: ticket.os,
      os_version: ticket.os_version,
      additional_details: ticket.additional_details
    });
  }

  deleteTicket(ticket: TechnicalSupportTicket): void {
    if (confirm(`Are you sure you want to delete ticket ${ticket.ticket_number}?`)) {
      this.loading = true;
      this.technicalSupportService.deleteTicket(ticket.id!).subscribe({
        next: (response) => {
          this.showSuccess('Ticket deleted successfully');
          this.loadTickets();
          this.loadStats();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error deleting ticket:', error);
          this.showError('Failed to delete ticket');
          this.loading = false;
        }
      });
    }
  }

  updateTicketStatus(ticket: TechnicalSupportTicket, newStatus: string): void {
    this.technicalSupportService.updateTicket(ticket.id!, { status: newStatus as any }).subscribe({
      next: (response) => {
        this.showSuccess(`Ticket status updated to ${newStatus}`);
        this.loadTickets();
        this.loadStats();
      },
      error: (error) => {
        console.error('Error updating ticket status:', error);
        this.showError('Failed to update ticket status');
      }
    });
  }

  // ==================== UTILITY METHODS ====================

  getStatusColor(status: string): string {
    return this.technicalSupportService.getStatusColor(status);
  }

  getPriorityColor(priority: string): string {
    return this.technicalSupportService.getPriorityColor(priority);
  }

  getTicketTypeColor(ticketType: string): string {
    return this.technicalSupportService.getTicketTypeColor(ticketType);
  }

  getStatusText(status: string): string {
    return this.technicalSupportService.getStatusText(status);
  }

  formatDate(dateString: string): string {
    return this.technicalSupportService.formatDate(dateString);
  }

  getTimeAgo(dateString: string): string {
    return this.technicalSupportService.getTimeAgo(dateString);
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  // Getter methods for form validation
  get firstName() { return this.ticketForm.get('first_name'); }
  get lastName() { return this.ticketForm.get('last_name'); }
  get email() { return this.ticketForm.get('email'); }
  get ticketType() { return this.ticketForm.get('ticket_type'); }
  get priority() { return this.ticketForm.get('priority'); }
  get browser() { return this.ticketForm.get('browser'); }
  get browserVersion() { return this.ticketForm.get('browser_version'); }
  get os() { return this.ticketForm.get('os'); }
  get osVersion() { return this.ticketForm.get('os_version'); }
  get additionalDetails() { return this.ticketForm.get('additional_details'); }
}